import React, {useEffect, useMemo, useRef, useState} from 'react'
import {Button, Col, Form, InputGroup, Row} from 'react-bootstrap'
import Select from 'react-select'
import _, {filter, find, includes, isUndefined, toNumber} from 'lodash'
import {reactSelectValue} from '../../utils/Helper'
import {
  DiscountType,
  EXPENSE_PAYMENT_ADJUSTMENT_OPTIONS,
  getProductPaymentAccountOptions,
  IncomeDocumentTypes,
  TAX_OPTIONS,
  VAT_OPTIONS,
} from '../../constants/Common'
import FormDiscountType from './FormDiscountType'
import {numberFormat} from '../../utils/Functions'
import api from '../../utils/Api'
import {useLocation, useParams, useRoutes} from 'react-router-dom'
import useCommon from '../../hook/useCommon'
import {IIncomeDocument} from '../app/IncomeDocumentDetailPage'
import DatePicker from '../DatePicker'
import moment from 'moment'
import {getAccounts} from '../../modules/accountant/core/_request'
import {computeAutoVat} from '../../utils/rrFormCalculations'

interface IFormExpenseProductItems {
  documentType?: string
  branchId?: number | string
  departmentId?: number | string
  requireScopeSelection?: boolean
  vatType: string
  disabledAddButton?: boolean
  accountCode?: string
  accountCodeId?: string
  fixAccountCode?: boolean
  locked?: boolean
  isCreditDebitNotePage?: boolean
  isNotOriginalDoc?: boolean
  isDebit?: boolean
  isCredit?: boolean
  isExpense?: boolean
  hideDeleteBtn?: boolean
  isShowAddAsset?: boolean
  useNewItemsInForm?: boolean
  showSelector?: boolean
  canSelectEditable?: boolean
  paymentId?: string | null
  previousDocument?: any
  previousItems?: any[]
  depositDeductionDocuments?: any[]
  allowExceedMaxQuantity?: boolean
  // เพดานจำนวนรายบรรทัด (ตำแหน่งตรงกับ previousItems) ใช้แทนจำนวนใน previousItems
  // เช่นแก้ไข PO ที่เปิดจาก PR: previousItems คือรายการของ PO เอง แต่เพดานต้องเป็นยอดคงเหลือใน PR
  // undefined ในบรรทัดไหน = บรรทัดนั้นไม่จำกัด
  maxQuantities?: (number | undefined)[]
  // Expense-only: enables the per-item editable VAT(บาท) cell + lock toggle.
  // Default false so shared renders (e.g. revenue-receipt) keep the original UI.
  enableVatOverride?: boolean
  onChange(data: any[], opts?: any): void
  onSelected?(data: any[]): void
}

const FormExpenseProductItems: React.FC<IFormExpenseProductItems> = (props) => {
  const {id} = useParams()
  const location = useLocation()
  const {useDebouncedEffect} = useCommon()
  const {
    documentType,
    canSelectEditable,
    disabledAddButton,
    locked,
    paymentId,
    previousDocument,
    depositDeductionDocuments = [],
  } = props
  const hasPreviousItemsProp = Array.isArray(props.previousItems)
  // ต้อง memo ไว้ ไม่งั้นเมื่อ parent ไม่ส่ง previousItems มา (เช่นเอกสารสร้างใหม่)
  // จะได้ array ใหม่ทุกครั้งที่ render → effect ที่ผูกกับ previousItems ทำงานทุก render
  // แล้ว setState ซ้ำจนวนไม่จบ
  const previousItems = useMemo(() => props.previousItems ?? [], [props.previousItems])
  const [productItems, setProductItems] = useState<any[]>([])
  const [productItemsAssetsIndex, setProductItemsAssetsIndex] = useState<any[]>([])
  const [productItemsOriginal, setProductItemsOriginal] = useState<any[]>([])
  const [productItemsMaxQuantity, setProductItemsMaxQuantity] = useState<(number | undefined)[]>([])
  const [treatmentItems, setTreatmentItems] = useState<any[]>([])
  const [options, setOptions] = useState({
    treatmentItems: [],
    treatmentItemsAssets: [],
    treatmentItemsNoneAssets: [],
  })
  const [selectedItemsIndex, setSelectedItemsIndex] = useState<any[]>([])
  const [rowSearchOptions, setRowSearchOptions] = useState<Record<number, any[]>>({})
  const [accountOptions, setAccountOptions] = useState<{label: string; value: string}[]>([])
  const fetchedAccountCodeProductIdsRef = useRef<Set<number>>(new Set())
  const previousInventoryScopeKeyRef = useRef<string | null>(null)
  const fetchProductsSeqRef = useRef<number>(0)
  const fetchProductsLatestSeqRef = useRef<number>(0)
  const currentPayment = find(previousDocument?.incomeDocumentPayments, {id: toNumber(paymentId)})
  const isInCredit = documentType === IncomeDocumentTypes.CREDIT
  const isInDebit = documentType === IncomeDocumentTypes.DEBIT
  const isInExpensePage = location.pathname.includes('expense')
  const isInExpensePurchaseDebitNote = location.pathname.includes('expense-purchase-debit-note')
  const isInExpenseDebitCreditPage =
    location.pathname.includes('expense') &&
    (location.pathname.includes('debit') || location.pathname.includes('credit'))
      ? true
      : false
  const isInExpenseRequestPurchaseOrder =
    documentType === IncomeDocumentTypes.EXPENSE_REQUEST_PURCHASE_ORDER
  const isInExpensePurchaseInventoryRecord =
    documentType === IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD
  const isInExpenseRecord = documentType === IncomeDocumentTypes.EXPENSE_EXPENSE_RECORD
  const isInExpenseDepositPayment = documentType === IncomeDocumentTypes.EXPENSE_DEPOSIT_PAYMENT
  const isInExpensePurchaseOrder = documentType === IncomeDocumentTypes.EXPENSE_PURCHASE_ORDER
  const isInExpensePurchaseAssetOrder =
    documentType === IncomeDocumentTypes.EXPENSE_PURCHASE_ASSET_ORDER ||
    location.pathname.includes('/expense-purchase-asset-order')

  // REQ-002: แถว "ค่าธรรมเนียม หรือรายการปรับปรุง" บนเอกสารบันทึกซื้อสินค้า
  // เป็นแถวที่ไม่ผูกสินค้า แต่ผูกกับผังบัญชีโดยตรง (ค่าขนส่ง ค่าธรรมเนียม ส่วนลดท้ายบิล)
  // จึงกระทบเฉพาะยอดเงิน/การลงบัญชี ไม่สร้าง inventory transaction และไม่กระทบสต็อก
  const getItemAccountCode = (item: any) =>
    typeof item?.accountCode === 'string' ? item.accountCode : item?.accountCode?.code || ''

  // แถวที่เพิ่มใหม่จะมี selectedType ติดมา ส่วนแถวที่โหลดจากเอกสารเดิมไม่มี
  // จึงต้องเดาจากรูปทรงข้อมูล (ไม่มีสินค้า แต่มีผังบัญชี) ซึ่งเกิดได้เฉพาะแถวปรับปรุงเท่านั้น
  const isAdjustmentRow = (item: any) =>
    item?.selectedType === 'adjustment' ||
    (isInExpensePurchaseInventoryRecord &&
      !item?.inventoryProductId &&
      !item?.treatmentItemId &&
      !!getItemAccountCode(item))
  // ใบลดหนี้/เพิ่มหนี้ที่ไม่อ้างอิงเอกสารเดิม แถวบัญชีคือรายการค่าใช้จ่ายตรง ๆ ไม่มีเรื่องเพิ่ม/ลด
  const isNoteAccountRow = (item: any) => !!props.isNotOriginalDoc && isAdjustmentRow(item)
  const isBlankProductRow = (item: any) =>
    !item?.selectedType &&
    !item?.inventoryProductId &&
    !item?.treatmentItemId &&
    !getItemAccountCode(item) &&
    !Number(item?.pricePerUnit)
  const treatmentAssetItems = treatmentItems.filter((item: any) => item.type === 'asset')
  const treatmentNotAssetItems = treatmentItems.filter((item: any) => item.type !== 'asset')
  const isAPExpense = previousDocument?.code?.startsWith('AP')
  const isDPExpense = previousDocument?.code?.startsWith('DP')
  const isRRExpense = previousDocument?.code?.startsWith('RR')
  const isInInvoice =
    documentType === IncomeDocumentTypes.INVOICE || location.pathname.includes('/invoice/')

  const buildInventoryProductLabel = (product: any) => {
    const base = `${product.code} - ${product.nameTh}`
    const warehouseLabel = product?.warehouseLabel

    if (!warehouseLabel) return base

    return `${base} (${warehouseLabel})`
  }

  const isUseServiceQuery =
    !isInInvoice &&
    !isInExpenseRequestPurchaseOrder &&
    !isInExpensePurchaseOrder &&
    !isRRExpense &&
    !isInExpensePurchaseInventoryRecord

  const defaultProductItem = {
    treatmentItemId: undefined,
    inventoryProductId: undefined,
    quantity: 0,
    pricePerUnit: 0,
    discount: 0,
    discountPerUnit: 0,
    discountType: DiscountType.FIXED,
    subTotal: 0,
    whTax: 0,
    whTaxPrice: 0,
    vat: 99,
    vatPrice: 0,
    vatLocked: true,
    note: '',
    accountCode: props.accountCode || '',
    accountCodeId: undefined,
  }

  const buildMaxQuantityState = (items: any[]) => {
    if (props.allowExceedMaxQuantity) {
      return items.map(() => undefined)
    }

    if (props.maxQuantities) {
      return items.map((_: any, index: number) => props.maxQuantities?.[index])
    }

    return items.map((item: any) => item.quantity)
  }

  // เพดานจาก parent มักมาทีหลัง (ต้องโหลดเอกสารต้นทางก่อน) จึงต้องตั้งเพดานใหม่เมื่อค่าเปลี่ยน
  useEffect(() => {
    if (!props.maxQuantities) return
    setProductItemsMaxQuantity(buildMaxQuantityState(previousItems))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.maxQuantities])

  // ใช้ inventory products สำหรับเอกสารซื้อสินค้า
  const isUseInventoryProducts =
    isInExpenseRequestPurchaseOrder ||
    isInExpensePurchaseOrder ||
    isInExpensePurchaseInventoryRecord ||
    isInExpensePurchaseAssetOrder

  // เอกสารซื้อสินค้าต้องเลือกสาขา/แผนกก่อน ระบบถึงจะโหลดรายการสินค้า (requireScopeSelection gate)
  // ถ้ายังไม่เลือก scope รายการจะว่างและไม่ยิง API → แจ้งให้ผู้ใช้เลือกก่อน
  const scopeNotSelected =
    isUseInventoryProducts && !!props.requireScopeSelection && !props.branchId && !props.departmentId

  // Helper function to validate Date object for DatePicker
  // Invalid Date object is truthy in JavaScript, so we must check with isNaN()
  const toValidDateForPicker = (dateValue: any): Date | null => {
    if (!dateValue) return null
    // Check if it's a Date object and not an Invalid Date
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue
    }
    // Check with moment for other values
    const m = moment(dateValue)
    return m.isValid() ? m.toDate() : null
  }

  const fetchProducts = (_params = {}, rowIndex?: number) => {
    if (isInExpenseRecord || isAPExpense || isDPExpense || isInExpenseDepositPayment) {
      getAccounts(_params).then((res: any) => {
        res = res.map((account: any) => ({
          ...account,
          name: account.name,
        }))
        const mapped: any = res.map((product: any) => ({
          ...product,
          label: `${product.code} - ${product.name}`,
          value: product.id,
        }))
        if (rowIndex !== undefined) {
          // อัปเดตเฉพาะ row ที่ค้นหา ไม่กระทบ row อื่น
          setRowSearchOptions((prev) => ({...prev, [rowIndex]: mapped}))
        } else {
          setOptions((opt: any) => ({
            ...opt,
            treatmentItems: mapped,
            treatmentItemsAssets: mapped,
            treatmentItemsNoneAssets: mapped,
          }))
        }

        setTreatmentItems(res)
      })
    } else if (isUseInventoryProducts) {
      if (props.requireScopeSelection && !props.branchId && !props.departmentId) {
        // Invalidate any in-flight scoped request so its late response can't
        // restore options for a scope the user has just cleared.
        fetchProductsSeqRef.current += 1
        fetchProductsLatestSeqRef.current = fetchProductsSeqRef.current
        setOptions((opt: any) => ({
          ...opt,
          treatmentItems: [],
          treatmentItemsAssets: [],
          treatmentItemsNoneAssets: [],
        }))
        setTreatmentItems([])
        return
      }

      // ใช้ inventory-products API สำหรับเอกสารซื้อสินค้า
      fetchProductsSeqRef.current += 1
      const seq = fetchProductsSeqRef.current
      fetchProductsLatestSeqRef.current = seq
      api.request(
        'inventory/inventory-products/for-dropdown',
        (res) => {
          // Drop stale responses so an earlier branch/department fetch
          // cannot pollute the newer scope's options.
          if (seq !== fetchProductsLatestSeqRef.current) return

          const mapped: any = res.map((product: any) => ({
            ...product,
            label: buildInventoryProductLabel(product),
            value: product.id,
            inventoryProductId: product.id,
            isInventoryProduct: true,
          }))
          const mappedAssetItems = res
            .filter((product: any) => ['asset', 'item-asset'].includes(product.type))
            .map((product: any) => ({
              ...product,
              label: buildInventoryProductLabel(product),
              value: product.id,
              inventoryProductId: product.id,
              isInventoryProduct: true,
            }))

          const mappedNoneAssetItems = res
            .filter((product: any) => product.type !== 'asset')
            .map((product: any) => ({
              ...product,
              label: buildInventoryProductLabel(product),
              value: product.id,
              inventoryProductId: product.id,
              isInventoryProduct: true,
            }))

          // Merge with existing options so items pinned by useEffect[previousItems]
          // (PR inventoryProduct) are not wiped by a late callback.
          setOptions((opt: any) => ({
            ...opt,
            treatmentItems: _.uniqBy(
              [...mapped, ...(opt.treatmentItems || [])],
              ({value}: any) => value
            ),
            treatmentItemsAssets: _.uniqBy(
              [...mappedAssetItems, ...(opt.treatmentItemsAssets || [])],
              ({value}: any) => value
            ),
            treatmentItemsNoneAssets: _.uniqBy(
              [...mappedNoneAssetItems, ...(opt.treatmentItemsNoneAssets || [])],
              ({value}: any) => value
            ),
          }))

          setTreatmentItems((prev: any[]) =>
            _.uniqBy(
              [...res.map((r: any) => ({...r, isInventoryProduct: true})), ...(prev || [])],
              ({id}: any) => id
            )
          )
        },
        {
          types: ['item', 'medicine', 'asset', 'item-asset', 'lab', 'work', 'accounting'],
          ...(props.requireScopeSelection ? {requireScopeSelection: true} : {}),
          ...(props.branchId ? {branchId: Number(props.branchId)} : {}),
          ...(props.departmentId ? {departmentId: Number(props.departmentId)} : {}),
          // In sub-department mode, do not force main warehouse.
          ...(props.departmentId ? {} : {onlyMainWarehouse: true}),
          s: _params,
        }
      )

      // เฟส A+: โหมดเอกสารซื้อรายจ่ายใช้ inventory products เท่านั้น
    } else {
      api.request(
        'treatment-item',
        (res) => {
          const mapped: any = res.map((product: any) => ({
            ...product,
            label: product.nameTh,
            value: product.id,
          }))
          const mappedAssetItems = res
            .filter((product: any) => product.type === 'asset')
            .map((product: any) => ({...product, label: product.nameTh, value: product.id}))

          const mappedNoneAssetItems = res
            .filter((product: any) => product.type !== 'asset')
            .map((product: any) => ({...product, label: product.nameTh, value: product.id}))

          setOptions((opt: any) => ({
            ...opt,
            treatmentItems: _.uniqBy([...options.treatmentItems, ...mapped], ({value}) => value),
            treatmentItemsAssets: _.uniqBy(
              [...options.treatmentItems, ...mappedAssetItems],
              ({value}) => value
            ),
            treatmentItemsNoneAssets: _.uniqBy(
              [...options.treatmentItems, ...mappedNoneAssetItems],
              ({value}) => value
            ),
          }))

          setTreatmentItems(res)
        },
        {
          forDropdown: true,
          types: isInExpenseRecord
            ? ['accounting', 'work']
            : props.isExpense || isInExpensePage
            ? isUseServiceQuery
              ? ['item', 'medicine', 'asset', 'lab', 'service', 'work']
              : ['item', 'medicine', 'asset', 'lab', 'work']
            : ['asset', 'accounting', 'work'],
          s: _params,
        }
      )
    }
  }

  const distributePaymentWithQuantity = (): number[] => {
    if (!previousDocument || !currentPayment) return []

    const unitPrices = previousDocument?.incomeDocumentItems.map(
      (item: any) => Number(item.pricePerUnit) - Number(item.discountPerUnit)
    )

    const extraWhTaxPrice =
      Array.isArray(currentPayment?.extraWithholdingTaxItems) &&
      currentPayment?.extraWithholdingTaxItems.length > 0
        ? currentPayment?.extraWithholdingTaxItems?.reduce(
            (sum: number, item: any) => sum + (Number(item?.whTaxPrice) || 0),
            0
          )
        : 0

    const quantities = previousDocument?.incomeDocumentItems.map((item: any) =>
      Number(item.quantity)
    )
    // const payment = previousDocument?.totalAmount
    const payment =
      Number(currentPayment?.amount) +
      (extraWhTaxPrice ? extraWhTaxPrice : Number(currentPayment?.whTaxPrice))

    const totalPrices = unitPrices.map((price: number, index: number) => price * quantities[index])
    const total = totalPrices.reduce((sum: number, price: number) => sum + price, 0)

    if (total === 0) {
      throw new Error('ราคารวมต้องมากกว่า 0')
    }

    const distributed = totalPrices.map((price: number) => (price / total) * payment)

    return distributed.map((amount: number, index: number) =>
      parseFloat((amount / quantities[index]).toFixed(4))
    )
  }

  useEffect(() => {
    fetchProducts('')
    setProductItems([...productItems, defaultProductItem])
    // Load account options
    getProductPaymentAccountOptions().then((options) => {
      setAccountOptions(options)
    })
  }, [])

  useEffect(() => {
    if (!isUseInventoryProducts) return

    // Treat falsy (null/undefined/''/0) branchId AND departmentId as
    // unresolved — matches the no-scope guard inside fetchProducts. Without
    // this the first render writes "0:0" into the ref, so the real
    // unresolved->value transition later looks like a real scope change and
    // wipes PR items.
    const isCurrentResolved = Boolean(props.branchId) || Boolean(props.departmentId)
    if (!isCurrentResolved) {
      fetchProducts('')
      return
    }

    const currentScopeKey = `${Number(props.branchId || 0)}:${Number(props.departmentId || 0)}`
    const previousScopeKey = previousInventoryScopeKeyRef.current
    previousInventoryScopeKeyRef.current = currentScopeKey

    // Skip reset on the first resolved scope. Resetting here wipes items that
    // useEffect[previousItems] just pinned from a related PR document, causing
    // the dropdown label to disappear intermittently.
    if (previousScopeKey !== null) {
      setOptions((opt: any) => ({
        ...opt,
        treatmentItems: [],
        treatmentItemsAssets: [],
        treatmentItemsNoneAssets: [],
      }))
      setTreatmentItems([])
    }
    fetchProducts('')

    if (previousScopeKey === null || previousScopeKey === currentScopeKey) return

    // In inventory mode treat treatmentItemId as a scoped identity too,
    // otherwise legacy rows (treatmentItemId only) survive scope changes.
    const isScopedRow = (item: any) =>
      Number(item?.inventoryProductId || 0) > 0 ||
      !!item?.inventoryProduct ||
      Number(item?.treatmentItemId || 0) > 0

    const hasScopedInventorySelections = productItems.some(isScopedRow)

    if (!hasScopedInventorySelections) return

    fetchedAccountCodeProductIdsRef.current.clear()
    setSelectedItemsIndex([])
    setProductItems((prev: any[]) =>
      prev.map((item: any) => {
        if (!isScopedRow(item)) return item

        // ล้างเฉพาะตัวตนสินค้า แต่คงจำนวน/ราคา/VAT/หมายเหตุไว้เพื่อไม่ให้ผู้ใช้ต้องกรอกใหม่ทั้งแถว
        return {
          ...item,
          treatmentItemId: undefined,
          inventoryProductId: undefined,
          inventoryProduct: undefined,
          ...(item?.incomeDocumentCode && {incomeDocumentCode: item.incomeDocumentCode}),
          ...(item?.branchId && {branchId: item.branchId}),
        }
      })
    )
  }, [props.branchId, props.departmentId, isUseInventoryProducts])

  useEffect(() => {
    props?.onSelected?.(selectedItemsIndex)
  }, [selectedItemsIndex])

  useEffect(() => {
    if (!previousItems?.length) {
      setProductItemsMaxQuantity([])
      return
    }

    // เพิ่ม inventoryProduct หรือ treatmentItem จาก previousItems เข้า options เพื่อให้ dropdown แสดงชื่อสินค้า
    if (isUseInventoryProducts) {
      const itemsFromPreviousItems = previousItems
        .map((item: any) => {
          // ใช้ inventoryProduct ก่อน
          if (item.inventoryProduct) {
            const product = item.inventoryProduct
            return {
              ...product,
              label: `${product.code} - ${product.nameTh}`,
              value: product.id,
              inventoryProductId: product.id,
              isInventoryProduct: true,
            }
          }
          // Fallback ไปใช้ treatmentItem (สำหรับเอกสารเก่าที่ยังไม่มี inventoryProductId)
          if (item.treatmentItem) {
            const ti = item.treatmentItem
            return {
              ...ti,
              label: `${ti.code || ''} - ${ti.nameTh || ti.name || ''}`.replace(/^- /, ''),
              value: ti.id,
              isInventoryProduct: false,
            }
          }
          return null
        })
        .filter(Boolean)

      if (itemsFromPreviousItems.length > 0) {
        setOptions((opt: any) => ({
          ...opt,
          treatmentItems: _.uniqBy(
            [...opt.treatmentItems, ...itemsFromPreviousItems],
            ({value}) => value
          ),
          treatmentItemsAssets: _.uniqBy(
            [...opt.treatmentItemsAssets, ...itemsFromPreviousItems],
            ({value}) => value
          ),
          treatmentItemsNoneAssets: _.uniqBy(
            [...opt.treatmentItemsNoneAssets, ...itemsFromPreviousItems],
            ({value}) => value
          ),
        }))

        setTreatmentItems((prev: any[]) =>
          _.uniqBy(
            [...prev, ...itemsFromPreviousItems.map((item: any) => ({...item, id: item.value}))],
            ({id}) => id
          )
        )
      }
    }

    const relateExpensePurchaseOrderDocuments = (previousDocument?.childrenDocuments || []).filter(
      (doc: IIncomeDocument) =>
        doc?.type === IncomeDocumentTypes.EXPENSE_PURCHASE_ORDER &&
        !['voided', 'cancelled'].includes(String(doc?.state || '').toLowerCase())
    )

    const productItemsPrepared = previousItems?.map((item, index) => {
      const itemQuantity = Number(item.quantity) || 0
      const itemDiscountPerUnit = Number(item.discountPerUnit) || 0
      const itemPricePerUnit = Number(item.pricePerUnit) || 0
      const itemWhTax = Number(item.whTax) || 0
      const itemWhTaxPrice = Number(item.whTaxPrice) || 0
      const itemVatPrice = Number(item.vatPrice) || 0

      const netPricePerUnit = itemPricePerUnit - itemDiscountPerUnit
      let pricePerUnit = currentPayment ? distributePaymentWithQuantity()[index] : itemPricePerUnit

      const subTotal =
        (currentPayment ? distributePaymentWithQuantity()[index] : netPricePerUnit) * itemQuantity

      const itemTotalBeforeVat = parseFloat(
        (
          (distributePaymentWithQuantity()[index] * 100) /
          (100 + (item.vat === 99 ? 0 : item.vat))
        ).toFixed(2)
      )

      const currentPaymentTotal =
        Number(currentPayment?.amount || 0) + Number(currentPayment?.whTaxPrice || 0)
      const netSubTotal =
        previousDocument?.vatType === 'included' && item.vat !== 'none'
          ? parseFloat(((subTotal * 100) / (100 + item.vat)).toFixed(2))
          : subTotal

      const isItemNoVat = [0, 99, 'none'].includes(item.vat)
      let _formPrepared = {
        ...(item?.incomeDocumentCode && {incomeDocumentCode: item.incomeDocumentCode}),
        // EX-006: บรรทัดนี้มาจากบรรทัดไหนของใบสั่งซื้อ — ส่งต่อไปถึง payload ตอนบันทึก
        // เพื่อให้ API เขียนยอดตัดของใบสั่งซื้อได้เอง ไม่ต้องให้หน้าจอเดาจับคู่ทีหลัง
        ...(item?.sourceIncomeDocumentItemId && {
          sourceIncomeDocumentItemId: item.sourceIncomeDocumentItemId,
        }),
        ...(item?.branchId && {branchId: item.branchId}),
        ...(item?.departmentId && {departmentId: item.departmentId}),
        treatmentItemId: item.treatmentItemId,
        inventoryProductId: item.inventoryProductId,
        inventoryProduct: item.inventoryProduct,
        accountCodeId: item.accountCodeId,
        quantity: itemQuantity,
        pricePerUnit: currentPayment
          ? pricePerUnit
          : isInCredit || isInDebit
          ? netPricePerUnit
          : pricePerUnit,
        discount: currentPayment || isInCredit || isInDebit ? 0 : itemDiscountPerUnit,
        discountPerUnit: currentPayment || isInCredit || isInDebit ? 0 : itemDiscountPerUnit,
        discountType: DiscountType.FIXED,
        whTax: itemWhTax,
        // whTaxPrice: currentPayment ? pricePerUnit * itemQuantity * (itemWhTax / 100) : itemWhTaxPrice,
        whTaxPrice: currentPayment ? itemTotalBeforeVat * (itemWhTax / 100) : itemWhTaxPrice,
        vat: item.vat,
        // Option D: seed VAT from the stored value so a manual override on the
        // source document is preserved on load (no recompute, no false-unlock).
        vatLocked: true,
        vatPrice: itemVatPrice,
        note: item.note || '',
        accountCode:
          props.accountCode ||
          item.accountCode ||
          item.accountCodeItem?.code ||
          item.inventoryProduct?.accountCode?.code,
        // ? parseFloat(netSubTotal.toFixed(2)) - (isItemNoVat ? 0 : parseFloat((netSubTotal * 0.07).toFixed(2)))
        subTotal: currentPayment
          ? itemTotalBeforeVat
          : previousDocument?.vatType === 'included' && item.vat !== 'none'
          ? netSubTotal
          : subTotal,
        expiryDate: item?.expiryDate,
        serialNo: item?.serialNo,
        isAccountCode: item.accountCode || item.accountCodeItem?.code ? true : false,
      }

      if (currentPayment) {
        const documentFullPrice = Number(previousDocument.totalAmount) || 0
        const paymentFeeIncrease = (previousDocument.incomeDocumentPaymentFees || []).reduce(
          (a: number, b: any) => (b.reason === '-1' ? a : a + Number(b.amount || 0)),
          0
        )
        const paymentFeeDecrease = (previousDocument.incomeDocumentPaymentFees || []).reduce(
          (a: number, b: any) => (b.reason === '1' ? a : a + Number(b.amount || 0)),
          0
        )

        const extraWhTaxPrice =
          Array.isArray(currentPayment?.extraWithholdingTaxItems) &&
          currentPayment?.extraWithholdingTaxItems.length > 0
            ? currentPayment?.extraWithholdingTaxItems?.reduce(
                (sum: number, item: any) => sum + (Number(item?.whTaxPrice) || 0),
                0
              )
            : 0

        const totalPayment =
          Number(currentPayment.amount || 0) +
          (extraWhTaxPrice ? extraWhTaxPrice : Number(currentPayment.whTaxPrice || 0)) +
          paymentFeeDecrease -
          paymentFeeIncrease
        const receivedPaymentPercent = (totalPayment / documentFullPrice) * 100
        const pricePerUnit = netPricePerUnit * (receivedPaymentPercent / 100)
        const subTotal = Number((pricePerUnit * itemQuantity).toFixed(2))

        _formPrepared = {
          ..._formPrepared,
          pricePerUnit: Number(pricePerUnit.toFixed(4)),
          subTotal: subTotal,
          whTaxPrice: subTotal * (itemWhTax / 100),
          // Payment-distribution recomputes the line amount; the source override
          // no longer matches it, so fall back to auto VAT (follow-when-matched).
          vatPrice: computeAutoVat(
            {
              subTotal,
              vat: item.vat,
              quantity: itemQuantity,
              pricePerUnit: Number(pricePerUnit.toFixed(4)),
              discountPerUnit: 0,
            },
            props.vatType
          ),
        }
      }

      _formPrepared = {
        ..._formPrepared,
        ...((isInExpenseRecord || isInExpenseDepositPayment) && {
          treatmentItemId: item?.accountCodeId || item?.treatmentItemId,
        }),
      }

      return _formPrepared
    })

    setProductItemsOriginal(productItemsPrepared)
    setProductItemsMaxQuantity(buildMaxQuantityState(productItemsPrepared))
    if (canSelectEditable) {
      setProductItems(
        previousItems.map((item: any) => {
          return {
            treatmentItemId: item.treatmentItemId,
            quantity: 0,
            pricePerUnit: 0,
            discount: 0,
            discountPerUnit: 0,
            discountType: DiscountType.FIXED,
            whTax: 0,
            whTaxPrice: 0,
            vat: 0,
            note: '',
            accountCode: props.accountCode || item.accountCode || '',
            accountCodeId: props.accountCodeId || item.accountCodeId || '',
            subTotal: 0,
          }
        })
      )

      return
    }

    if (relateExpensePurchaseOrderDocuments) {
      const issuedItems = relateExpensePurchaseOrderDocuments
        ?.map((doc: IIncomeDocument) => {
          return doc.incomeDocumentItems
        })
        ?.flat()

      // For PO: use ordered consumption to handle partial qty per item line correctly
      const isPO = documentType === IncomeDocumentTypes.EXPENSE_PURCHASE_ORDER
      if (isPO) {
        // Use inventoryProductId as matching key (unique per line), fallback to treatmentItemId
        const getItemKey = (item: any) =>
          item.inventoryProductId ? String(item.inventoryProductId) : String(item.treatmentItemId)

        const remainingPerItem = productItemsPrepared.map((item) => ({
          key: getItemKey(item),
          remaining: item.quantity,
        }))

        for (const issueItem of issuedItems || []) {
          const key = getItemKey(issueItem)
          let qty = Number(issueItem.quantity || 0)
          for (const ri of remainingPerItem) {
            if (ri.key !== key || ri.remaining <= 0) continue
            const deduct = Math.min(ri.remaining, qty)
            ri.remaining -= deduct
            qty -= deduct
            if (qty <= 0) break
          }
        }

        const filtered = productItemsPrepared
          .map((prevItem, index) => {
            const remainingQty = remainingPerItem[index].remaining
            if (remainingQty <= 0) {
              return {...prevItem, quantity: 0}
            }
            const partialSubTotal =
              remainingQty * (prevItem.pricePerUnit - prevItem.discountPerUnit)
            return {
              ...prevItem,
              quantity: remainingQty,
              subTotal: partialSubTotal,
              // Partial-quantity copy: the source override no longer matches this
              // line amount, so recompute auto VAT (follow-when-matched rule).
              vatPrice: computeAutoVat(
                {
                  subTotal: partialSubTotal,
                  vat: prevItem.vat,
                  quantity: remainingQty,
                  pricePerUnit: prevItem.pricePerUnit,
                  discountPerUnit: prevItem.discountPerUnit,
                },
                props.vatType
              ),
            }
          })
          .filter((prevItem) => prevItem.quantity > 0)

        setProductItems(filtered)
        setProductItemsMaxQuantity(buildMaxQuantityState(filtered))
        return
      }

      // Default: filter out items that have matching treatmentItemId (original logic)
      const filtered = productItemsPrepared.filter(
        (prevItem) =>
          !issuedItems.some((issueItem: any) => issueItem.treatmentItemId === prevItem.treatmentItemId)
      )
      setProductItems(filtered)
      setProductItemsMaxQuantity(buildMaxQuantityState(filtered))
      return
    }
    setProductItems(productItemsPrepared)
    setProductItemsMaxQuantity(buildMaxQuantityState(productItemsPrepared))
  }, [previousItems, canSelectEditable])

  useEffect(() => {}, [selectedItemsIndex])

  useEffect(() => {
    if (!isUseInventoryProducts || !productItems.length || !treatmentItems.length) return

    let hasChanged = false
    const nextItems = productItems.map((item: any) => {
      if (item.accountCode) return item

      const productId = Number(item.inventoryProductId || item.treatmentItemId)
      if (!productId) return item

      const matched = find(treatmentItems, (t: any) => Number(t.id) === productId)
      const matchedAccountCode =
        matched?.accountCode?.code ||
        (typeof matched?.accountCode === 'string' ? matched.accountCode : '')

      if (!matchedAccountCode) return item

      hasChanged = true
      return {
        ...item,
        accountCode: String(matchedAccountCode),
      }
    })

    if (hasChanged) {
      setProductItems(nextItems)
    }
  }, [isUseInventoryProducts, productItems, treatmentItems])

  useEffect(() => {
    if (!isUseInventoryProducts || !productItems.length) return

    const missingProductIds = Array.from(
      new Set(
        productItems
          .filter((item: any) => !item.accountCode && Number(item.inventoryProductId))
          .map((item: any) => Number(item.inventoryProductId))
          .filter((id) => !fetchedAccountCodeProductIdsRef.current.has(id))
      )
    )

    if (!missingProductIds.length) return

    missingProductIds.forEach((id) => fetchedAccountCodeProductIdsRef.current.add(id))

    Promise.all(
      missingProductIds.map(
        (productId) =>
          new Promise<{productId: number; code: string}>((resolve) => {
            api.request(`inventory/inventory-products/${productId}`, (res: any) => {
              resolve({
                productId,
                code: String(res?.data?.accountCode?.code || ''),
              })
            })
          })
      )
    ).then((rows) => {
      const accountCodeByProductId = new Map<number, string>()
      rows.forEach(({productId, code}) => {
        if (code) {
          accountCodeByProductId.set(productId, code)
        }
      })

      if (!accountCodeByProductId.size) return

      setProductItems((prev: any[]) =>
        prev.map((item: any) => {
          if (item.accountCode) return item
          const code = accountCodeByProductId.get(Number(item.inventoryProductId))
          if (!code) return item
          return {
            ...item,
            accountCode: code,
          }
        })
      )
    })
  }, [isUseInventoryProducts, productItems])

  useDebouncedEffect(
    () => {
      const originalItems = treatmentItems.filter((optionItem: any) => {
        return productItems.some((formItem: any) => {
          if (optionItem?.isInventoryProduct) {
            return formItem.inventoryProductId === optionItem.id
          }

          return formItem.treatmentItemId === optionItem.id
        })
      })
      const opts = {
        hasWorkItem: originalItems.some((x) => x.type === 'work'),
      }

      const mapped = productItems.map((item) => ({
        ...item,
        // Ensure numeric values are numbers before sending
        quantity: Number(item.quantity) || 0,
        pricePerUnit: Number(item.pricePerUnit) || 0,
        discount: Number(item.discount) || 0,
        discountPerUnit: Number(item.discountPerUnit) || 0,
        discountType: Number(item.discountType),
        whTax: Number(item.whTax) || 0,
        whTaxPrice: Number(item.whTaxPrice) || 0,
        vat: Number(item.vat),
        subTotal: Number(item.subTotal) || 0,
        // vatPrice is the source of truth the parent reads. Locked items carry
        // either the auto value (recomputed on edit) or the DB-seeded value
        // (preserved on load); unlocked items carry the manual override.
        vatPrice: Number.isFinite(Number(item.vatPrice))
          ? Number(item.vatPrice)
          : computeAutoVat(item, props.vatType),
        ...(item?.expiryDate && {expiryDate: moment(item.expiryDate).format('YYYY-MM-DD')}),
      }))

      let filtered = props?.showSelector
        ? selectedItemsIndex.length
          ? mapped.filter((item, index) => selectedItemsIndex?.includes(index))
          : []
        : mapped

      filtered = filtered.map(({accountCode, ...rest}) =>
        accountCode ? {accountCode, ...rest} : rest
      )

      props.onChange(filtered, opts)
    },
    [productItems, props?.showSelector, selectedItemsIndex],
    500
  )

  useEffect(() => {
    if (props.vatType === 'none') {
      setProductItems((prev: any) => {
        return [
          ...prev.map((item: any) => {
            return {
              ...item,
              vat: 99,
              vatPrice: 0,
              subTotal: Number(
                (
                  Number(item.quantity) *
                  (Number(item.pricePerUnit) - Number(item.discountPerUnit))
                ).toFixed(2)
              ),
            }
          }),
        ]
      })

      return
    }

    setProductItems((prev: any) => {
      return [
        ...prev.map((item: any) => {
          if (item.vat === 'none') return item

          const subTotal =
            Number(item.quantity) * (Number(item.pricePerUnit) - Number(item.discountPerUnit))
          const itemVat = Number(item.vat)
          const netSubTotal =
            props.vatType === 'excluded'
              ? subTotal
              : ((subTotal === 0 ? 1 : subTotal) * 100) / (100 + (itemVat === 99 ? 0 : itemVat))

          const nextSubTotal = Number(netSubTotal.toFixed(2))

          return {
            ...item,
            subTotal: nextSubTotal,
            whTaxPrice: netSubTotal * (Number(item.whTax) / 100),
            // Locked items follow the auto VAT under the new vatType; unlocked
            // (manual) overrides are preserved.
            vatPrice: item.vatLocked
              ? computeAutoVat(
                  {
                    subTotal: nextSubTotal,
                    vat: itemVat,
                    quantity: Number(item.quantity),
                    pricePerUnit: Number(item.pricePerUnit),
                    discountPerUnit: Number(item.discountPerUnit),
                  },
                  props.vatType
                )
              : item.vatPrice,
          }
        }),
      ]
    })
  }, [props.vatType])

  const taxOptionsResolved = filter(TAX_OPTIONS, (v) => v.value !== 99)

  // The editable-VAT cell adds a 7th field column, which crowds the row. When
  // the feature is on, give each field an explicit responsive width so they
  // wrap into tidy rows instead of squeezing. Gated on enableVatOverride so
  // shared renders that don't use the feature (e.g. revenue) keep their layout.
  const fieldColSizing = props.enableVatOverride ? {xs: 6, lg: 2} : {}

  return (
    <div className='mt-3'>
      {productItems.map((item: any, index: number) => {
        let currentDepositDeductionItem: any
        const isInSelectedItem = includes(selectedItemsIndex, index)
        const _setFormProduct = (newValue: object) => {
          const newProductItems = productItems.map((product: any, productIndex: number) => {
            return productIndex !== index
              ? product
              : {
                  ...product,
                  ...newValue,
                }
          })

          setProductItems(newProductItems)
        }

        const onDateChange = (date: Date, e: any, name: string) => {
          _setFormProduct({
            expiryDate: date,
          })
        }

        const isAdjustment = isAdjustmentRow(item)
        const isAccountRow = isNoteAccountRow(item)
        // ทิศทางการปรับปรุง: เก็บเป็นเครื่องหมายของจำนวนเงิน (ลบ = ปรับลดยอด)
        const adjustmentSign = Number(item?.pricePerUnit) < 0 ? -1 : 1

        // คำนวณยอดของแถวใหม่เมื่อราคา/จำนวนเงินเปลี่ยน (ใช้ร่วมกันระหว่างช่องราคา
        // และปุ่มเลือกเพิ่ม/ลดของแถวปรับปรุง)
        const applyPricePerUnit = (value: number) => {
          const discountPerUnit =
            item.discountType === DiscountType.PERCENT
              ? value * ((Number(item.discount) * 10) / 1000)
              : Number(item.discount)
          const netPricePerUnit = value - discountPerUnit
          const netPriceSubTotal = netPricePerUnit * item.quantity
          const subTotal =
            paymentId || (props.vatType === 'included' && item.vat !== 'none')
              ? parseFloat(
                  (
                    (netPriceSubTotal * 100) /
                    (100 + (Number(item.vat) === 99 ? 0 : Number(item.vat)))
                  ).toFixed(2)
                )
              : netPriceSubTotal

          _setFormProduct({
            pricePerUnit: value,
            discountPerUnit: discountPerUnit,
            subTotal: subTotal,
            whTaxPrice: subTotal * (Number(item.whTax) / 100),
            vatPrice: item.vatLocked
              ? computeAutoVat(
                  {
                    subTotal,
                    vat: item.vat,
                    quantity: Number(item.quantity),
                    pricePerUnit: value,
                    discountPerUnit,
                  },
                  props.vatType
                )
              : item.vatPrice,
          })
        }

        const _renderRelateDepositInfo = () => {
          return depositDeductionDocuments.map((depositDoc: any, index: number) => {
            const specificItem = find(depositDoc.incomeDocumentItems, {
              treatmentItemId: item.treatmentItemId,
            })

            return (
              <div className={'px-3 py-2 rounded-2'} style={{background: '#f4f3f3'}}>
                <strong>{`รายการมัดจำที่ ${index + 1} : ${depositDoc.code}`}</strong>
                {specificItem ? (
                  <div>
                    <span>{'จำนวนเงินที่หักมัดจำของสินค้านี้ :'}</span>
                    <strong className={'ms-1 text-primary'}>
                      {`${numberFormat(
                        (specificItem.pricePerUnit + specificItem.discountPerUnit) *
                          specificItem.quantity
                      )} บาท`}
                    </strong>
                  </div>
                ) : (
                  <div className={'text-muted'}>{'ไม่มีการหักมัดจำให้สินค้านี้'}</div>
                )}
              </div>
            )
          })
        }

        const currentTreatmentItem =
          item?.treatmentItemId && !item?.inventoryProductId
            ? find(
                treatmentItems,
                (t) => Number(t.id) === Number(item.treatmentItemId) && !t.isInventoryProduct
              ) || find(treatmentItems, (t) => Number(t.id) === Number(item.treatmentItemId))
            : find(
                treatmentItems,
                (t) => Number(t.id) === Number(item?.inventoryProductId || item?.treatmentItemId)
              )

        if (currentTreatmentItem?.accountCodeId && !currentTreatmentItem?.accountCode) {
          api.request(
            'account/account-code',
            (res) => {
              if (!isUndefined(res)) {
                _setFormProduct({
                  accountCode: String(res?.code || ''),
                })
                currentTreatmentItem.accountCode = res?.code
              }
            },
            {id: currentTreatmentItem?.accountCodeId}
          )
        } else if (
          (currentTreatmentItem?.accountCode?.code ||
            (typeof currentTreatmentItem?.accountCode === 'string'
              ? currentTreatmentItem.accountCode
              : undefined)) &&
          !item.accountCode
        ) {
          // Populate accountCode from the linked inventory product when it's not set on the item
          _setFormProduct({
            accountCode: String(
              currentTreatmentItem?.accountCode?.code || currentTreatmentItem?.accountCode || ''
            ),
          })
        }

        return (
          <Row key={`product-item-${index}`} className={'gx-1 gy-2 custom-item-card'}>
            {!!item.incomeDocumentCode && <h3 className='m-0'>{item.incomeDocumentCode}</h3>}

            {!props.isDebit && !!props?.showSelector && (
              <Col xs={12}>
                <Form.Group>
                  <Form.Check
                    type='checkbox'
                    checked={includes(selectedItemsIndex, index)}
                    id={`item-checkbox-${index}`}
                    className='custom-checkbox'
                    onChange={() => {
                      if (includes(selectedItemsIndex, index)) {
                        setSelectedItemsIndex((prev) => {
                          return prev.filter((prevIndex) => prevIndex !== index)
                        })
                      } else {
                        setSelectedItemsIndex([...selectedItemsIndex, index])
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            )}
            {props.isDebit &&
              previousItems.some((v) => v.treatmentItemId === item?.treatmentItemId) &&
              !!props?.showSelector && (
                <Col xs={12}>
                  <Form.Group>
                    <Form.Check
                      type='checkbox'
                      checked={includes(selectedItemsIndex, index)}
                      id={`item-checkbox-${index}`}
                      className='custom-checkbox'
                      onChange={() => {
                        if (includes(selectedItemsIndex, index)) {
                          setSelectedItemsIndex((prev) => {
                            return prev.filter((prevIndex) => prevIndex !== index)
                          })
                        } else {
                          setSelectedItemsIndex([...selectedItemsIndex, index])
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
              )}

            {isInExpenseRequestPurchaseOrder && (
              <Col xs={12} lg={3}>
                <Form.Group className='mb-2'>
                  <Form.Label>{'รหัสสินค้า'}</Form.Label>
                  <Form.Control
                    disabled
                    value={currentTreatmentItem?.code || '-'}
                    name={`item_quantity_${index}`}
                  />
                </Form.Group>
              </Col>
            )}

            {isAccountRow && (
              <Col xs={12} lg={4}>
                <Form.Group className='mb-2'>
                  <Form.Label>
                    {`บัญชี/ค่าใช้จ่าย (${index + 1})`}
                    <span className='text-danger'>*</span>
                  </Form.Label>
                  <Select
                    classNamePrefix='react-select'
                    className={'text-primary fw-bold'}
                    options={accountOptions}
                    name={`note_account_code_${index}`}
                    placeholder='พิมพ์เพื่อค้นหาบัญชี/ค่าใช้จ่าย'
                    value={reactSelectValue(accountOptions, getItemAccountCode(item))}
                    onChange={({value}: any) => {
                      _setFormProduct({
                        accountCode: String(value),
                      })
                    }}
                  />
                </Form.Group>
              </Col>
            )}

            {isAdjustment && !isAccountRow && (
              <Col xs={12} lg={4}>
                <Form.Group className='mb-2'>
                  <Form.Label>
                    {`ค่าธรรมเนียม หรือรายการปรับปรุง (${index + 1})`}{' '}
                    <span className='text-danger'>*</span>
                  </Form.Label>
                  <Select
                    isDisabled={
                      !props.isNotOriginalDoc &&
                      (locked || currentPayment || props?.isCredit || props?.isDebit)
                    }
                    classNamePrefix='react-select'
                    className={'text-primary fw-bold'}
                    options={EXPENSE_PAYMENT_ADJUSTMENT_OPTIONS}
                    name={`adjustment_reason_${index}`}
                    value={reactSelectValue(EXPENSE_PAYMENT_ADJUSTMENT_OPTIONS, adjustmentSign)}
                    onChange={(opt: any) => {
                      applyPricePerUnit(
                        Math.abs(Number(item.pricePerUnit) || 0) * Number(opt?.value || 1)
                      )
                    }}
                  />
                </Form.Group>
              </Col>
            )}

            <Col xs={12} lg={4} className={isAdjustment ? 'd-none' : ''}>
              <Form.Group className='mb-2'>
                <Form.Label>
                  {item.selectedType === 'asset'
                    ? 'สินทรัพย์'
                    : isInExpenseRecord || isInExpenseDepositPayment
                    ? 'รายการ'
                    : 'สินค้า'}{' '}
                  ({index + 1}) <span className='text-danger'>*</span>
                  {canSelectEditable && (
                    <Button
                      size={'sm'}
                      className={'py-0 px-2 ms-3'}
                      variant={includes(selectedItemsIndex, index) ? 'danger' : 'primary'}
                      onClick={() => {
                        if (includes(selectedItemsIndex, index)) {
                          setSelectedItemsIndex((prev) => {
                            return prev.filter((prevIndex) => prevIndex !== index)
                          })
                          setProductItems((prev) => {
                            const updatedItems = [...prev]
                            updatedItems[index] = {
                              treatmentItemId: item.treatmentItemId,
                              quantity: 0,
                              pricePerUnit: 0,
                              discount: 0,
                              discountPerUnit: 0,
                              discountType: DiscountType.FIXED,
                              whTax: 0,
                              whTaxPrice: 0,
                              vat: 0,
                              note: '',
                              accountCode: props.accountCode || item.accountCode || '',
                              accountCodeId: props.accountCodeId || item.accountCodeId || '',
                              subTotal: 0,
                            }

                            return updatedItems
                          })

                          return
                        }

                        setSelectedItemsIndex([...selectedItemsIndex, index])
                        setProductItems((prev) => {
                          const updatedItems = [...prev]
                          updatedItems[index] = productItemsOriginal[index]

                          return updatedItems
                        })
                      }}
                    >
                      {includes(selectedItemsIndex, index) ? 'เอาออก' : 'เลือกรายการ'}
                    </Button>
                  )}
                </Form.Label>
                <Select
                  isDisabled={
                    !props.isNotOriginalDoc &&
                    (locked || currentPayment || props?.isCredit || props?.isDebit)
                  }
                  classNamePrefix='react-select'
                  className={'text-primary fw-bold'}
                  options={
                    isInExpenseRequestPurchaseOrder ||
                    isInExpensePurchaseOrder ||
                    isInExpensePurchaseInventoryRecord
                      ? productItemsAssetsIndex.includes(index)
                        ? options.treatmentItemsAssets
                        : options.treatmentItemsNoneAssets
                      : rowSearchOptions[index] ?? options.treatmentItems
                  }
                  name={`treatmentItem_${index}`}
                  placeholder={
                    scopeNotSelected
                      ? 'เลือกสาขา/แผนกก่อนเลือกสินค้า'
                      : `พิมพ์เพื่อค้นหา${
                          isInExpenseRecord || isAPExpense || isDPExpense || isInExpenseDepositPayment
                            ? 'บัญชี/ค่าใช้จ่าย'
                            : item.selectedType === 'asset'
                            ? 'สินทรัพย์'
                            : 'สินค้า'
                        }`
                  }
                  onChange={(opt, action) => {
                    const rawValue = opt?.value
                    // treatment items ใช้ prefix 'ti_' เพื่อไม่ให้ id ชนกับ inventory products
                    const isTreatmentItemOption =
                      typeof rawValue === 'string' && String(rawValue).startsWith('ti_')
                    const value = isTreatmentItemOption
                      ? Number(String(rawValue).replace('ti_', ''))
                      : rawValue
                    const product = isTreatmentItemOption
                      ? (find(treatmentItems, {id: value, isInventoryProduct: false}) as any)
                      : (find(treatmentItems, {id: value}) as any)
                    const isInventoryProduct = product?.isInventoryProduct

                    // Convert price values to numbers to prevent string issues
                    const buyingPrice = Number(product?.buyingPrice) || 0
                    const normalPriceMin = Number(product?.normalPriceMin) || 0
                    const pricePerUnit = buyingPrice || normalPriceMin

                    _setFormProduct({
                      ...defaultProductItem,
                      ...(item?.incomeDocumentCode && {
                        incomeDocumentCode: item.incomeDocumentCode,
                      }),
                      ...(item?.branchId && {branchId: item.branchId}),
                      // สำหรับ inventory products, บันทึก inventoryProductId
                      ...(isInventoryProduct && {
                        inventoryProductId: value,
                        inventoryProduct: product,
                        treatmentItemId: undefined,
                      }),
                      // สำหรับ treatment items, บันทึก treatmentItemId
                      ...(!isInventoryProduct &&
                        !isInExpenseRecord &&
                        !isInExpenseDepositPayment &&
                        !isAPExpense &&
                        !isDPExpense && {treatmentItemId: value, inventoryProductId: undefined}),
                      ...((isInExpenseDepositPayment ||
                        isInExpenseRecord ||
                        isAPExpense ||
                        isDPExpense) && {
                        accountCodeId: value,
                      }),
                      // Auto-fill accountCode from inventory product
                      ...(isInventoryProduct &&
                        (product?.accountCode?.code || product?.accountCode) && {
                          accountCode: product?.accountCode?.code || String(product?.accountCode),
                        }),
                      quantity: 1,
                      pricePerUnit: pricePerUnit,
                      subTotal: pricePerUnit,
                    })
                  }}
                  onInputChange={(s, meta) => {
                    if (meta.action !== 'input-change') return s

                    const keyword = s.trim()
                    if (isInExpenseRecord || isAPExpense || isDPExpense || isInExpenseDepositPayment) {
                      if (!keyword) {
                        // เคลียร์ per-row options เมื่อลบ keyword
                        setRowSearchOptions((prev) => {
                          const next = {...prev}
                          delete next[index]
                          return next
                        })
                      } else {
                        fetchProducts(keyword, index)
                      }
                    } else {
                      fetchProducts(keyword)
                    }
                    return s
                  }}
                  noOptionsMessage={({inputValue}) => {
                    if (scopeNotSelected) return 'กรุณาเลือกสาขา/แผนกก่อนเลือกสินค้า'
                    if (inputValue.length >= 2) return 'ไม่พบข้อมูล'
                    return 'ค้นหาสินค้าโดยการพิมพ์ 2 ตัวอักษรขึ้นไป'
                  }}
                  value={(() => {
                    const selectedId =
                      isInExpenseRecord || isAPExpense || isDPExpense || isInExpenseDepositPayment
                        ? item['accountCodeId'] ?? item['treatmentItemId']
                        : isUseInventoryProducts
                        ? Number(item['inventoryProductId'] ?? item['treatmentItemId']) || undefined
                        : item['treatmentItemId']

                    const option = reactSelectValue(options.treatmentItems, selectedId)
                    if (option) return option

                    if (isUseInventoryProducts && item?.inventoryProduct?.id) {
                      const product = item.inventoryProduct
                      return {
                        value: Number(product.id),
                        label: `${product.code || ''} - ${
                          product.nameTh || product.nameEn || ''
                        }`.replace(/^\s*-\s*/, ''),
                      }
                    }

                    return null
                  })()}
                />
              </Form.Group>
              {/* แสดง Ref เลขที่ PO สำหรับ บันทึกซื้อ (RR) จากหลาย PO */}
              {item.incomeDocumentCode && (
                <div style={{marginTop: '-12px', marginBottom: '6px', marginLeft: '4px'}}>
                  <span className='text-muted fs-8'>Ref: {item.incomeDocumentCode}</span>
                </div>
              )}
            </Col>

            <Col {...fieldColSizing} className={isAdjustment ? 'd-none' : ''}>
              <Form.Group className='mb-5' controlId={`item_quantity_${index}`}>
                <Form.Label>จำนวน</Form.Label>
                <Form.Control
                  disabled={
                    !props.isNotOriginalDoc &&
                    ((locked && !isInSelectedItem) ||
                      currentPayment ||
                      ((props?.isCredit || props?.isDebit) &&
                        !includes(selectedItemsIndex, index)) ||
                      (typeof productItemsMaxQuantity[index] === 'number' &&
                        !productItemsMaxQuantity[index]))
                  }
                  type='number'
                  min={0}
                  max={
                    props.allowExceedMaxQuantity
                      ? undefined
                      : productItemsMaxQuantity[index] || undefined
                  }
                  value={item.quantity}
                  name={`item_quantity_${index}`}
                  className={'text-primary fw-bold'}
                  onChange={(val) => {
                    let value = val.target.value === '' ? 0 : toNumber(val.target.value)
                    const maxQuantity = productItemsMaxQuantity[index]

                    // จำกัดไม่ให้เกินจำนวนเริ่มต้น (ยกเว้นกรณีรับสินค้าเพิ่ม)
                    if (maxQuantity && value > maxQuantity && !props.allowExceedMaxQuantity) {
                      value = maxQuantity
                    }

                    const netPricePerUnit = Number(item.pricePerUnit) - Number(item.discountPerUnit)
                    const subTotal =
                      props.vatType === 'included' && item.vat !== 'none'
                        ? parseFloat(
                            (
                              (netPricePerUnit * value * 100) /
                              (100 + (Number(item.vat) === 99 ? 0 : Number(item.vat)))
                            ).toFixed(2)
                          )
                        : netPricePerUnit * value

                    _setFormProduct({
                      quantity: value,
                      subTotal: subTotal,
                      whTaxPrice: subTotal * (Number(item.whTax) / 100),
                      vatPrice: item.vatLocked
                        ? computeAutoVat(
                            {
                              subTotal,
                              vat: item.vat,
                              quantity: value,
                              pricePerUnit: Number(item.pricePerUnit),
                              discountPerUnit: Number(item.discountPerUnit),
                            },
                            props.vatType
                          )
                        : item.vatPrice,
                    })
                  }}
                />
                {typeof productItemsMaxQuantity[index] === 'number' && (
                  <Form.Text className='text-muted'>
                    จำนวนสูงสุด: {productItemsMaxQuantity[index]}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col {...fieldColSizing} className={isInExpenseRequestPurchaseOrder ? 'd-none' : ''}>
              <Form.Group className='mb-5' controlId={`item_price_per_unit_${index}`}>
                <Form.Label>{isAccountRow ? 'จำนวนเงิน' : isAdjustment ? 'จำนวนเงินที่ปรับปรุง' : 'ราคา/หน่วย'}</Form.Label>
                <Form.Control
                  disabled={
                    !props.isNotOriginalDoc &&
                    ((locked && !isInSelectedItem) ||
                      ((props?.isCredit || props?.isDebit) && !includes(selectedItemsIndex, index)))
                  }
                  type={'number'}
                  min={isAdjustment ? 0 : undefined}
                  value={isAdjustment ? Math.abs(Number(item.pricePerUnit) || 0) : item.pricePerUnit}
                  name={`item_price_per_unit_${index}`}
                  className={'text-primary fw-bold'}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : Number(e.target.value)

                    // แถวปรับปรุงกรอกจำนวนเงินเป็นบวกเสมอ แล้วให้ช่อง "ปรับปรุง" เป็นตัวกำหนดทิศทาง
                    applyPricePerUnit(isAdjustment ? Math.abs(value) * adjustmentSign : value)
                  }}
                />
              </Form.Group>
            </Col>
            <Col
              {...fieldColSizing}
              className={isInExpenseRequestPurchaseOrder || isAdjustment ? 'd-none' : ''}
            >
              <Form.Group className='mb-0' controlId={`discount_${index}`}>
                <Form.Label>ส่วนลด/หน่วย</Form.Label>
                <Form.Control
                  disabled={
                    !props.isNotOriginalDoc &&
                    (locked ||
                      currentPayment ||
                      props?.isCredit ||
                      ((props?.isCredit || props?.isDebit) && !includes(selectedItemsIndex, index)))
                  }
                  value={item.discount}
                  name={`discount_${index}`}
                  className={'text-primary fw-bold'}
                  onChange={(val) => {
                    const inputValue = val.target.value
                    const rawValue = inputValue === '' ? 0 : parseFloat(inputValue)

                    if (isNaN(rawValue)) return

                    const discountPerUnit =
                      Number(item.discountType) === DiscountType.PERCENT
                        ? Number(item.pricePerUnit) * (rawValue / 100)
                        : rawValue

                    const netPricePerUnit = Number(item.pricePerUnit) - discountPerUnit
                    const subTotal = netPricePerUnit * Number(item.quantity)
                    const netSubTotal =
                      props.vatType === 'included' && item.vat !== 'none'
                        ? parseFloat(
                            (
                              (subTotal * 100) /
                              (100 + (Number(item.vat) === 99 ? 0 : Number(item.vat)))
                            ).toFixed(2)
                          )
                        : subTotal

                    _setFormProduct({
                      discount: inputValue,
                      discountPerUnit: discountPerUnit,
                      subTotal: netSubTotal,
                      whTaxPrice: netSubTotal * (Number(item.whTax) / 100),
                      vatPrice: item.vatLocked
                        ? computeAutoVat(
                            {
                              subTotal: netSubTotal,
                              vat: item.vat,
                              quantity: Number(item.quantity),
                              pricePerUnit: Number(item.pricePerUnit),
                              discountPerUnit,
                            },
                            props.vatType
                          )
                        : item.vatPrice,
                    })
                  }}
                />
              </Form.Group>
              {!currentPayment && (
                <FormDiscountType
                  disabled={
                    !props.isNotOriginalDoc &&
                    (locked ||
                      props?.isCredit ||
                      ((props?.isCredit || props?.isDebit) && !includes(selectedItemsIndex, index)))
                  }
                  value={item.discountType}
                  name={`treatmentItem_discount_type_${index}`}
                  onChange={(val) => {
                    const newDiscountType = Number(val.target.value)
                    const discountPerUnit =
                      newDiscountType === DiscountType.PERCENT
                        ? Number(item.pricePerUnit) * ((Number(item.discount) * 10) / 1000)
                        : Number(item.discount)

                    const netPricePerUnit = Number(item.pricePerUnit) - discountPerUnit
                    const subTotal = netPricePerUnit * Number(item.quantity)
                    const netSubTotal =
                      props.vatType === 'included' && item.vat !== 'none'
                        ? parseFloat(
                            (
                              (subTotal * 100) /
                              (100 + (Number(item.vat) === 99 ? 0 : Number(item.vat)))
                            ).toFixed(2)
                          )
                        : subTotal

                    _setFormProduct({
                      discountType: newDiscountType,
                      discountPerUnit: discountPerUnit,
                      subTotal: netSubTotal,
                      whTaxPrice: netSubTotal * (Number(item.whTax) / 100),
                      vatPrice: item.vatLocked
                        ? computeAutoVat(
                            {
                              subTotal: netSubTotal,
                              vat: item.vat,
                              quantity: Number(item.quantity),
                              pricePerUnit: Number(item.pricePerUnit),
                              discountPerUnit,
                            },
                            props.vatType
                          )
                        : item.vatPrice,
                    })
                  }}
                />
              )}
            </Col>
            <Col {...fieldColSizing} className={isInExpenseRequestPurchaseOrder ? 'd-none' : ''}>
              <Form.Group className='mb-5' controlId={`vat_${index}`}>
                <Form.Label>ภาษี</Form.Label>
                <Select
                  isDisabled={
                    !props.isNotOriginalDoc &&
                    (locked ||
                      (!props?.isCredit && props.vatType === 'none') ||
                      currentPayment ||
                      ((props?.isCredit || props?.isDebit) && !includes(selectedItemsIndex, index)))
                  }
                  classNamePrefix='react-select'
                  options={VAT_OPTIONS}
                  name={`vat_${index}`}
                  className={'text-primary fw-bold'}
                  value={reactSelectValue(VAT_OPTIONS, item.vat)}
                  onChange={({value}) => {
                    const vatValue = typeof value === 'string' ? Number(value) : value
                    const netPricePerUnit = Number(item.pricePerUnit) - Number(item.discountPerUnit)
                    const subTotal = netPricePerUnit * Number(item.quantity)
                    const netSubTotal =
                      props.vatType === 'included' && value !== 'none'
                        ? parseFloat(((subTotal * 100) / (100 + vatValue)).toFixed(2))
                        : subTotal

                    const nextAutoVat = computeAutoVat(
                      {
                        subTotal: netSubTotal,
                        vat: vatValue,
                        quantity: Number(item.quantity),
                        pricePerUnit: Number(item.pricePerUnit),
                        discountPerUnit: Number(item.discountPerUnit),
                      },
                      props.vatType
                    )

                    _setFormProduct({
                      vat: vatValue,
                      subTotal: netSubTotal,
                      whTaxPrice: netSubTotal * (Number(item.whTax) / 100),
                      // Leaving 7% removes the override cell, so relock and drop
                      // any manual value (computeAutoVat = 0 for non-7) to avoid a
                      // stale override leaking onto a non-VAT line. Staying at 7%
                      // keeps a manual override while unlocked.
                      ...(Number(vatValue) !== 7
                        ? {vatLocked: true, vatPrice: nextAutoVat}
                        : {vatPrice: item.vatLocked ? nextAutoVat : item.vatPrice}),
                    })
                  }}
                />
              </Form.Group>
            </Col>
            <Col {...fieldColSizing} className={isInExpenseRequestPurchaseOrder ? 'd-none' : ''}>
              <Form.Group className='mb-5' controlId={`treatmentItem_before_tax_${index}`}>
                <Form.Label>มูลค่าก่อนหักภาษี</Form.Label>
                <Form.Control
                  readOnly
                  disabled={
                    !props.isNotOriginalDoc &&
                    (locked || currentPayment || props?.isCredit || props?.isDebit)
                  }
                  value={item.subTotal}
                  className={'text-primary fw-bold'}
                  name={`treatmentItem_before_tax_${index}`}
                />
              </Form.Group>
            </Col>
            {props.enableVatOverride && Number(item.vat) === 7 && (
              <Col {...fieldColSizing} className={isInExpenseRequestPurchaseOrder ? 'd-none' : ''}>
                <Form.Group className='mb-5' controlId={`item_vat_price_${index}`}>
                  <Form.Label>VAT (บาท)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type='number'
                      readOnly={item.vatLocked}
                      disabled={
                        !props.isNotOriginalDoc &&
                        (locked || currentPayment || props?.isCredit || props?.isDebit)
                      }
                      value={
                        item.vatLocked ? computeAutoVat(item, props.vatType) : item.vatPrice ?? 0
                      }
                      className={'text-primary fw-bold'}
                      name={`item_vat_price_${index}`}
                      onChange={(e) =>
                        _setFormProduct({
                          vatPrice: e.target.value === '' ? 0 : Number(e.target.value),
                        })
                      }
                    />
                    <Button
                      type='button'
                      variant={item.vatLocked ? 'primary' : 'dark'}
                      disabled={
                        !props.isNotOriginalDoc &&
                        (locked || currentPayment || props?.isCredit || props?.isDebit)
                      }
                      onClick={() =>
                        // Toggle lock; either way snap vatPrice to the auto value
                        // (แก้ไข = unlock + seed input, คืนค่า = lock + reset to auto).
                        _setFormProduct({
                          vatLocked: !item.vatLocked,
                          vatPrice: computeAutoVat(item, props.vatType),
                        })
                      }
                    >
                      {item.vatLocked ? 'แก้ไข' : 'คืนค่า'}
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            )}
            <Col {...fieldColSizing} className={isInExpenseRequestPurchaseOrder ? 'd-none' : ''}>
              <Form.Group className='mb-5' controlId={`wh_tax_${index}`}>
                <Form.Label>หัก ณ ที่จ่าย</Form.Label>
                <Select
                  isDisabled={
                    !props.isNotOriginalDoc &&
                    (locked || currentPayment || props?.isCredit || props?.isDebit)
                  }
                  classNamePrefix='react-select'
                  className={'text-primary fw-bold'}
                  options={taxOptionsResolved}
                  name={`wh_tax_${index}`}
                  value={reactSelectValue(taxOptionsResolved, item.whTax)}
                  onChange={({value}) => {
                    const whTaxValue = Number(value)
                    _setFormProduct({
                      whTax: whTaxValue,
                      whTaxPrice: Number(item.subTotal) * (whTaxValue / 100),
                    })
                  }}
                />
                <span>
                  รวม <strong>{numberFormat(item.whTaxPrice)}</strong>
                  {currentPayment && (
                    <>
                      <Form.Control
                        value={item.whTaxPrice}
                        className={'text-primary fw-bold'}
                        name={`treatmentItem_before_tax_${index}`}
                        onChange={(e) =>
                          _setFormProduct({
                            whTaxPrice: e.target.value === '' ? 0 : Number(e.target.value),
                          })
                        }
                      />
                      <span className={'text-danger'}>{'ใช้สำหรับแก้เลขเท่านั้น'}</span>
                    </>
                  )}
                </span>
              </Form.Group>
            </Col>

            {!isInExpenseRecord &&
              !isAPExpense &&
              !isDPExpense &&
              !isInExpenseDepositPayment &&
              !isAdjustment &&
              item.selectedType !== 'asset' && (
                <Col xs={12}>
                  <Row className={'gx-3'}>
                    <Col md={6} lg={4}>
                      <Form.Group controlId={`item_quantity_${index}`}>
                        <Form.Label>วันหมดอายุสินค้า</Form.Label>
                        <DatePicker
                          disabled={
                            !props.isNotOriginalDoc &&
                            (locked || currentPayment || props?.isCredit || props?.isDebit)
                          }
                          onChange={onDateChange}
                          name={`item_expiry_date_${index}`}
                          selected={toValidDateForPicker(item.expiryDate)}
                          dateFormat='dd/MM/yyyy'
                          timeCaption='time'
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode='select'
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6} lg={4}>
                      <Form.Group controlId={`item_quantity_${index}`}>
                        <Form.Label>หมายเลขสินทรัพย์</Form.Label>
                        <Form.Control
                          disabled={
                            !props.isNotOriginalDoc &&
                            (locked || currentPayment || props?.isCredit || props?.isDebit)
                          }
                          value={item.serialNo}
                          name={`item_serial_no_${index}`}
                          onChange={(e) => {
                            _setFormProduct({
                              serialNo: e.target.value,
                            })
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
              )}

            <Col xs={12}>
              <Row className={'gx-3'}>
                {!isInExpenseRecord && !isAPExpense && !isDPExpense && !isInExpenseDepositPayment && (
                  <Col md={4} className={isInExpenseRequestPurchaseOrder || isAccountRow ? 'd-none' : ''}>
                    <Form.Group className='mb-2'>
                      <Form.Label>
                        {isAdjustment ? 'ปรับปรุงด้วยบัญชี' : 'บัญชี (Account Code)'}
                        {isAdjustment && <span className='text-danger'> *</span>}
                      </Form.Label>
                      <Select
                        isDisabled={
                          !props.isNotOriginalDoc &&
                          (locked ||
                            props.fixAccountCode ||
                            currentPayment ||
                            props?.isCredit ||
                            props?.isDebit)
                        }
                        classNamePrefix='react-select'
                        options={accountOptions}
                        name={`account_code_${index}`}
                        value={reactSelectValue(
                          accountOptions,
                          (typeof item.accountCode === 'string'
                            ? item.accountCode
                            : item.accountCode?.code) ||
                            currentTreatmentItem?.accountCode?.code?.toString() ||
                            currentTreatmentItem?.code?.toString()
                        )}
                        onChange={({value}) => {
                          _setFormProduct({
                            accountCode: String(value),
                          })
                        }}
                      />
                    </Form.Group>
                  </Col>
                )}
                <Col md={8}>
                  <div className='d-flex'>
                    <Form.Group className='mb-2 w-75'>
                      <Form.Label>
                        {isInExpenseRecord || isInExpenseDepositPayment || isAdjustment ? (
                          <>คำอธิบายรายการ</>
                        ) : (
                          <>หมายเหตุ{item.selectedType === 'asset' ? 'สินทรัพย์' : 'สินค้า'}</>
                        )}
                      </Form.Label>
                      <Form.Control
                        as='textarea'
                        rows={2}
                        disabled={
                          !props.isNotOriginalDoc &&
                          (locked ||
                            currentPayment ||
                            ((props?.isCredit || props?.isDebit) &&
                              !includes(selectedItemsIndex, index)))
                        }
                        value={item.note}
                        name={`item_note_${index}`}
                        onChange={(e) => {
                          _setFormProduct({
                            note: e.target.value,
                          })
                        }}
                      />
                    </Form.Group>

                    {!props?.hideDeleteBtn && (
                      <>
                        {!props.isDebit &&
                          !props.isCreditDebitNotePage &&
                          !locked &&
                          productItems.length > 1 && (
                            <Form.Group className='my-auto d-flex justify-content-end w-25'>
                              <Button
                                size={'sm'}
                                variant={'danger'}
                                className={'px-3 py-2'}
                                onClick={() => {
                                  setProductItems([
                                    ...productItems.slice(0, index),
                                    ...productItems.slice(index + 1),
                                  ])

                                  setProductItemsMaxQuantity([
                                    ...productItemsMaxQuantity.slice(0, index),
                                    ...productItemsMaxQuantity.slice(index + 1),
                                  ])

                                  setProductItemsAssetsIndex((prevItems) =>
                                    prevItems.filter((item) => item !== index)
                                  )
                                }}
                              >
                                {'ลบ'}
                              </Button>
                            </Form.Group>
                          )}
                        {props.isDebit &&
                          !previousItems.some((v) => v.treatmentItemId === item?.treatmentItemId) &&
                          !locked &&
                          productItems.length > 1 && (
                            <Form.Group className='my-auto d-flex justify-content-end w-25'>
                              <Button
                                size={'sm'}
                                variant={'danger'}
                                className={'px-3 py-2'}
                                onClick={() => {
                                  setProductItems([
                                    ...productItems.slice(0, index),
                                    ...productItems.slice(index + 1),
                                  ])

                                  setProductItemsMaxQuantity([
                                    ...productItemsMaxQuantity.slice(0, index),
                                    ...productItemsMaxQuantity.slice(index + 1),
                                  ])

                                  setProductItemsAssetsIndex((prevItems) =>
                                    prevItems.filter((item) => item !== index)
                                  )
                                }}
                              >
                                {'ลบ'}
                              </Button>
                            </Form.Group>
                          )}
                      </>
                    )}
                  </div>
                </Col>

                {/* {depositDeductionDocuments.length ? (
                  <Col xs={12} md={4}>
                    {_renderRelateDepositInfo()}
                  </Col>
                ) : (
                  <></>
                )} */}
              </Row>
            </Col>
          </Row>
        )
      })}
      {(props?.isDebit
        ? true
        : !props.isCreditDebitNotePage && !locked && (!disabledAddButton || !currentPayment)) && (
        <div className={'d-flex gap-2'}>
          <Button
            size={'sm'}
            variant={'primary'}
            onClick={() => {
              setProductItems([...productItems, defaultProductItem])
              setProductItemsMaxQuantity([...productItemsMaxQuantity, undefined])
            }}
          >
            {isInExpenseRecord || isInExpenseDepositPayment ? 'เพิ่มรายการ' : 'เพิ่มสินค้า'}
          </Button>
          {(isInExpenseRequestPurchaseOrder ||
            isInExpensePurchaseOrder ||
            props?.isShowAddAsset) && (
            <Button
              size={'sm'}
              variant={'primary'}
              onClick={() => {
                setProductItems([...productItems, {...defaultProductItem, selectedType: 'asset'}])
                setProductItemsMaxQuantity([...productItemsMaxQuantity, undefined])

                setProductItemsAssetsIndex((prev) => {
                  return [...prev, productItems.length]
                })
              }}
            >
              {'เพิ่มสินทรัพย์'}
            </Button>
          )}
          {isInExpensePurchaseInventoryRecord && (
            <Button
              size={'sm'}
              variant={'outline-primary'}
              onClick={() => {
                const accountRow = {...defaultProductItem, selectedType: 'adjustment', quantity: 1}
                // แถวสินค้าว่างที่ฟอร์มเปิดมาให้ ใช้เป็นแถวบัญชีไปเลย ไม่ต้องให้ผู้ใช้ตามลบเอง
                if (
                  props.isNotOriginalDoc &&
                  productItems.length === 1 &&
                  isBlankProductRow(productItems[0])
                ) {
                  setProductItems([accountRow])
                  return
                }
                setProductItems([...productItems, accountRow])
                setProductItemsMaxQuantity([...productItemsMaxQuantity, undefined])
              }}
            >
              <i className='fa fa-plus fs-9 text-primary' />
              {props.isNotOriginalDoc
                ? 'เพิ่มรายการบัญชี/ค่าใช้จ่าย'
                : 'เพิ่มค่าธรรมเนียม หรือรายการปรับปรุง'}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default FormExpenseProductItems
